// Add these imports at the top
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@moe/oss/ui/card";
import { StatusButton } from "@moe/oss/ui/status-button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@moe/priv/components/ui/input-otp";
import { useMutation } from "@tanstack/react-query";
import { sb } from "@web/lib/supabase";
import { useAppContext } from "@web/route-services/root/AppContext";
import { REGEXP_ONLY_DIGITS_AND_CHARS } from "input-otp";
import { useState } from "react";
import { toast } from "sonner";

export function DeleteAccountModal() {
  const { access, profile, session } = useAppContext();
  const [otp, setOTP] = useState("");

  const startDeleteMutation = useMutation({
    mutationFn: async () => await access.account.startDelete(),
    onSuccess: () => {
      toast.success("Verification code sent to your email");
    },
    onError: () => {
      toast.error("Failed to start account deletion");
    }
  });

  const submitDeleteMutation = useMutation({
    mutationFn: async (code: string) => await access.account.submitDelete(code),
    onSuccess: async () => {
      toast.success("Your account has been deleted ^-^. Redirecting to homepage in 5 seconds...");
      await sb.auth.signOut();
      setTimeout(() => {
        window.location.href = "/";
      }, 5000);
    },
    onError: (e) => {
      toast.error(`Error submitting verification code: ${e.message}`);
    }
  });

  const deleteAccount = async (otp: string) => {
    if (!profile) throw new Error("Profile not found");
    if (otp.length < 8) return;
    const usernameCheck = prompt(`Type your username "${profile.username}" to confirm account deletion.`);
    if (!usernameCheck) return;
    if (usernameCheck === profile.username) {
       
      if (confirm("Are you sure want to delete your account? THIS CANNOT BE UNDONE.")) {
        submitDeleteMutation.mutate(otp);
      }
    } else {
      toast.error("You entered the wrong username. You may try again with the same verification code.");
    }
  };

  return (
    <Card className="text-tx-primary bg-float w-full max-w-xl border-none md:w-96 md:border">
      <CardHeader>
        <CardTitle>Delete {profile?.username ?? "Account"}</CardTitle>
        <CardDescription>
          Click "Send Verification Code" to send an account deletion code to your email.
        </CardDescription>
      </CardHeader>

      <CardContent>
        <StatusButton
          statusConfig={{
            idle: { text: "Send Verification Code" },
            pending: { text: "Sending code..." },
            success: {
              text: "Code sent!",
              tooltip: `Check ${session?.user?.email ?? "your email"} for the verification code.`
            },
            error: { text: "Failed to send", tooltip: "Unable to send verification code." }
          }}
          status={startDeleteMutation.status}
          onClick={() => startDeleteMutation.mutate()}
          variant="clear"
          className="px-0 font-semibold"
        />

        <InputOTP
          value={otp}
          pattern={REGEXP_ONLY_DIGITS_AND_CHARS}
          inputMode="text"
          onChange={(value) => {
            setOTP(value);
            if (value.length === 8) setTimeout(() => deleteAccount(value), 150);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") setTimeout(() => deleteAccount(otp), 150);
          }}
          maxLength={8}
        >
          <InputOTPGroup>
            {Array.from({ length: 8 }).map((_, i) => (
              <InputOTPSlot className="text-tx-primary font-mono text-lg font-semibold" key={i} index={i} />
            ))}
          </InputOTPGroup>
        </InputOTP>
      </CardContent>
    </Card>
  );
}
